/**
 * Утилиты для разбора ошибок и преобразования их в AppError
 */

import type { PostgrestError } from '@supabase/supabase-js';
import { type AppError, ErrorCode } from './types';
import type { ErrorType } from '@/types';
import {
  createNetworkError,
  createTimeoutError,
  createUnauthorizedError,
  createForbiddenError,
  createDatabaseError,
  createUnknownError,
} from './errorFactory';
import i18next from 'i18next';

/**
 * Проверяет, является ли ошибка AppError
 */
export function isAppError(error: unknown): error is AppError {
  return (
    error instanceof Error &&
    'code' in error &&
    typeof (error as AppError).code === 'string'
  );
}

/**
 * Проверяет, является ли ошибка Postgrest ошибкой
 */
function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'details' in error
  );
}

/**
 * Парсит Supabase/Postgrest ошибку в AppError
 */
function parseSupabaseError(error: PostgrestError): AppError {
  // RLS violation (permission denied)
  if (error.code === 'PGRST301' || error.message.includes('permission denied')) {
    return createForbiddenError(
      i18next.t('common:errors.forbidden'),
      error
    );
  }

  // Row not found
  if (error.code === 'PGRST116') {
    return createDatabaseError(i18next.t('common:errors.dataNotFound'), error);
  }

  // Общая ошибка БД
  return createDatabaseError(undefined, error);
}

/**
 * Преобразует любую ошибку в AppError
 */
export function parseError(error: unknown): AppError {
  // Уже AppError - возвращаем как есть
  if (isAppError(error)) {
    return error;
  }

  // Postgrest ошибка
  if (isPostgrestError(error)) {
    return parseSupabaseError(error);
  }

  // Стандартная Error
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Сетевые ошибки
    if (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('connection')
    ) {
      return createNetworkError(error);
    }

    // Таймаут
    if (message.includes('timeout') || message.includes('timed out')) {
      return createTimeoutError(error);
    }

    // 401 Unauthorized
    if (message.includes('unauthorized') || message.includes('401')) {
      return createUnauthorizedError(error);
    }

    // 403 Forbidden
    if (message.includes('forbidden') || message.includes('403')) {
      return createForbiddenError(undefined, error);
    }
  }

  // Неизвестная ошибка
  return createUnknownError(error);
}

/**
 * Получает тип ошибки для ErrorState компонента
 */
export function getErrorType(error: AppError): ErrorType {
  switch (error.code) {
    case ErrorCode.NETWORK_ERROR:
    case ErrorCode.TIMEOUT:
      return 'network';

    case ErrorCode.UNAUTHORIZED:
    case ErrorCode.FORBIDDEN:
    case ErrorCode.RLS_VIOLATION:
      return 'permission';

    case ErrorCode.DATABASE_ERROR:
    case ErrorCode.STORAGE_ERROR:
      return 'server';

    default:
      return 'error';
  }
}

/**
 * Получает человекопонятное сообщение об ошибке
 */
export function getErrorMessage(error: unknown): string {
  const appError = parseError(error);
  return appError.message;
}

/**
 * Получает детали ошибки для отладки (только в dev)
 */
export function getErrorDetails(error: unknown): string | undefined {
  if (!import.meta.env.DEV) {
    return undefined;
  }

  const appError = parseError(error);
  
  const parts: string[] = [
    `Code: ${appError.code}`,
    `Message: ${appError.message}`,
  ];

  if (appError.details) {
    parts.push(`Details: ${JSON.stringify(appError.details, null, 2)}`);
  }

  if (appError.originalError instanceof Error) {
    parts.push(`Original: ${appError.originalError.message}`);
    if (appError.originalError.stack) {
      parts.push(`Stack: ${appError.originalError.stack}`);
    }
  }

  return parts.join('\n\n');
}